(() => {
  if (customElements.get('brand-product')) return;

  class BrandProduct extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.signal = this.controller.signal;
      this.form = this.querySelector('.brand-product__form');
      this.radios = Array.from(this.querySelectorAll('input[name="id"][data-pack]'));
      this.button = this.querySelector('[data-brand-add]');
      this.status = this.querySelector('[data-brand-product-status]');
      this.error = this.querySelector('[data-brand-product-error]');
      this.cartLink = this.querySelector('[data-brand-product-cart-link]');
      this.afterpay = this.querySelector('[data-brand-afterpay]');
      this.gallery = this.querySelector('[data-brand-gallery]');
      this.slides = Array.from(this.querySelectorAll('[data-brand-gallery-slide]'));
      this.thumbnails = Array.from(this.querySelectorAll('[data-brand-thumbnail]'));
      this.dialog = this.querySelector('[data-brand-demo-dialog]');
      this.busy = false;

      if (this.form) {
        this.form.addEventListener('submit', this.submit.bind(this), { signal: this.signal });
        this.form.addEventListener('change', (event) => {
          if (event.target.matches('input[name="id"][data-pack]')) this.updateSelection(true);
        }, { signal: this.signal });
        this.applyUrlSelection();
        this.updateSelection(false);
      }

      this.setupGallery();
      this.setupDialog();
      window.BrandStore?.hydrate(this);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.querySelectorAll('video').forEach((video) => video.pause());
    }

    message(key, values = {}) {
      let message = this.dataset[key] || '';
      Object.entries(values).forEach(([name, value]) => {
        message = message.replaceAll(`{${name}}`, String(value));
      });
      return message;
    }

    money(cents) {
      return window.BrandStore?.money(cents) || new Intl.NumberFormat('en-AU', {
        style: 'currency', currency: 'AUD'
      }).format(cents / 100);
    }

    selected() {
      return this.radios.find((radio) => radio.checked && !radio.disabled);
    }

    applyUrlSelection() {
      const params = new URLSearchParams(window.location.search);
      const pack = params.get('pack');
      const variant = params.get('variant');
      const chosen = this.radios.find((radio) => !radio.disabled && radio.dataset.pack === pack)
        || this.radios.find((radio) => !radio.disabled && radio.value === variant);
      if (chosen) chosen.checked = true;
    }

    updateSelection(updateUrl) {
      if (!this.button || this.busy) return;
      const selected = this.selected();
      const available = selected?.dataset.available === 'true';
      const price = Number(selected?.dataset.price);
      this.button.disabled = !available;
      this.button.textContent = selected && Number.isSafeInteger(price)
        ? this.message('addWithPrice', { price: this.money(price) })
        : this.message('addText');
      this.error.textContent = '';
      this.error.hidden = true;
      this.cartLink.hidden = true;
      this.status.textContent = selected && !available ? this.message('unavailableText') : '';

      if (this.afterpay) {
        this.afterpay.hidden = !selected || !Number.isSafeInteger(price);
        if (!this.afterpay.hidden) {
          this.afterpay.textContent = this.message('afterpayText', { amount: this.money(Math.ceil(price / 4)) });
        }
      }

      if (updateUrl && selected && this.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', selected.value);
        url.searchParams.delete('pack');
        window.history.replaceState(window.history.state, '', url);
      }
    }

    async submit(event) {
      const store = window.BrandStore;
      if (!store) return;
      event.preventDefault();
      if (this.busy) return;
      const selected = this.selected();
      if (!selected || selected.dataset.available !== 'true') {
        this.status.textContent = this.message(selected ? 'unavailableText' : 'chooseText');
        return;
      }

      const variantId = Number(selected.value);
      if (!Number.isSafeInteger(variantId) || variantId <= 0) {
        this.status.textContent = this.message('chooseText');
        return;
      }

      const trigger = event.submitter || this.button;
      let committed = false;
      this.setBusy(true);
      this.error.hidden = true;
      this.cartLink.hidden = true;
      this.status.textContent = this.message('addingText');

      try {
        await store.enqueue(async () => {
          const result = await store.request('cart/add.js', {
            items: [{ id: variantId, quantity: 1 }],
            sections: ['brand-cart-drawer', 'cart-icon-bubble'],
            sections_url: window.location.pathname
          });
          committed = true;
          this.status.textContent = this.message('addedText');
          if (window.BrandCart?.refreshAndOpen) {
            await window.BrandCart.refreshAndOpen(result, trigger);
          } else {
            window.location.assign(this.dataset.cartUrl);
          }
        });
      } catch (error) {
        this.error.textContent = this.message(committed ? 'cartAddedText' : 'cartUnconfirmedText');
        this.error.hidden = false;
        this.cartLink.hidden = false;
        this.status.textContent = '';
      } finally {
        this.setBusy(false);
      }
    }

    setBusy(busy) {
      this.busy = busy;
      this.form.setAttribute('aria-busy', String(busy));
      this.radios.forEach((radio) => {
        if (busy) {
          radio.dataset.previouslyDisabled = String(radio.disabled);
          radio.disabled = true;
        } else {
          radio.disabled = radio.dataset.previouslyDisabled === 'true';
          delete radio.dataset.previouslyDisabled;
        }
      });
      if (busy) {
        this.button.disabled = true;
        this.button.textContent = this.message('addingText');
      } else {
        const selected = this.selected();
        this.button.disabled = selected?.dataset.available !== 'true';
        this.button.textContent = selected
          ? this.message('addWithPrice', { price: this.money(Number(selected.dataset.price)) })
          : this.message('addText');
      }
    }

    setupGallery() {
      if (!this.gallery || this.slides.length < 2) return;
      this.previous = this.querySelector('[data-brand-gallery-prev]');
      this.next = this.querySelector('[data-brand-gallery-next]');
      this.counter = this.querySelector('[data-brand-gallery-count]');
      this.querySelectorAll('[data-brand-gallery-controls]').forEach((element) => { element.hidden = false; });
      this.previous.addEventListener('click', () => this.goToSlide(this.slideIndex - 1), { signal: this.signal });
      this.next.addEventListener('click', () => this.goToSlide(this.slideIndex + 1), { signal: this.signal });
      this.thumbnails.forEach((thumbnail, index) => {
        thumbnail.addEventListener('click', () => this.goToSlide(index), { signal: this.signal });
      });
      this.gallery.addEventListener('scroll', () => {
        if (this.frame) cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.updateGallery());
      }, { passive: true, signal: this.signal });
      this.gallery.addEventListener('keydown', (event) => {
        if (event.target !== this.gallery) return;
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.goToSlide(this.slideIndex + (event.key === 'ArrowRight' ? 1 : -1));
      }, { signal: this.signal });
      this.resizeObserver = new ResizeObserver(() => this.updateGallery());
      this.resizeObserver.observe(this.gallery);
      this.updateGallery();
    }

    goToSlide(index) {
      const safeIndex = Math.max(0, Math.min(index, this.slides.length - 1));
      const target = this.slides[safeIndex];
      const targetLeft = this.gallery.scrollLeft + target.getBoundingClientRect().left - this.gallery.getBoundingClientRect().left;
      this.gallery.scrollTo({
        left: targetLeft,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
      });
    }

    updateGallery() {
      const left = this.gallery.getBoundingClientRect().left;
      let smallest = Infinity;
      let index = 0;
      this.slides.forEach((slide, position) => {
        const distance = Math.abs(slide.getBoundingClientRect().left - left);
        if (distance < smallest) {
          smallest = distance;
          index = position;
        }
      });
      this.slideIndex = index;
      this.previous.disabled = index === 0;
      this.next.disabled = index === this.slides.length - 1;
      this.counter.textContent = this.message('galleryCounter', { current: index + 1, total: this.slides.length });
      this.thumbnails.forEach((thumbnail, position) => {
        thumbnail.setAttribute('aria-current', position === index ? 'true' : 'false');
      });
      this.slides.forEach((slide, position) => {
        if (position !== index) slide.querySelectorAll('video').forEach((video) => video.pause());
      });
    }

    setupDialog() {
      if (!this.dialog) return;
      const openButton = this.querySelector('[data-brand-demo-open]');
      if (!openButton) return;
      openButton.hidden = false;
      openButton.addEventListener('click', () => {
        if (document.querySelector('dialog[open]')) return;
        this.dialogTrigger = document.activeElement;
        this.querySelectorAll('[data-brand-gallery] video').forEach((video) => video.pause());
        this.dialog.showModal();
        this.dialog.querySelector('[data-brand-demo-close]').focus();
      }, { signal: this.signal });
      this.dialog.querySelector('[data-brand-demo-close]').addEventListener('click', () => this.dialog.close(), { signal: this.signal });
      this.dialog.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const bounds = this.dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) {
          this.dialog.close();
        }
      }, { signal: this.signal });
      this.dialog.addEventListener('close', () => {
        this.dialog.querySelectorAll('video').forEach((video) => video.pause());
        if (this.dialogTrigger?.isConnected) this.dialogTrigger.focus();
      }, { signal: this.signal });
    }
  }

  customElements.define('brand-product', BrandProduct);
})();
